import * as Yup from 'yup';

/**
 * Validation for user-supplied name fields that are interpolated into
 * transactional emails (first name, last name, organization name).
 *
 * This mirrors the platform rule so Super Admin flags a bad name in the form
 * instead of submitting it and surfacing the backend's rejection as an
 * unrelated error. It is NOT a security control. The enforcing copy lives in
 * `nest-backend/src/common/validation/name-field.validator.ts` and the platform
 * copy in `frontend/src/utils/nameFieldValidation.ts`; all three must stay in sync.
 */

/**
 * Which character set applies to a given field.
 */
export enum NameFieldProfile {
  /** People's names: letters, marks, spaces, hyphen, apostrophe, period. */
  Person = 'person',
  /** Organization and goal names: the person set plus digits, & , ( ). */
  Label = 'label',
}

// Built with the constructor because the tsconfig target (es5) rejects the `u` flag on literals.
/* eslint-disable prefer-regex-literals */
const ALLOWED_CHARACTERS: Record<NameFieldProfile, RegExp> = {
  [NameFieldProfile.Person]: new RegExp("^[\\p{L}\\p{M} '’.-]*$", 'u'),
  [NameFieldProfile.Label]: new RegExp("^[\\p{L}\\p{M}\\p{N} '’.,&()-]*$", 'u'),
};
/* eslint-enable prefer-regex-literals */

const MAX_LENGTH: Record<NameFieldProfile, number> = {
  [NameFieldProfile.Person]: 100,
  [NameFieldProfile.Label]: 200,
};

/**
 * Matches a period followed by a TLD-like run of letters, which is what makes
 * `evil.com` a link even without a scheme. A trailing period (`Inc.`) and
 * single-letter initials (`J.R.`) do not match.
 */
const BARE_DOMAIN = /\.[a-zA-Z]{2,24}/;

/**
 * Checks a name field for links and characters that cannot appear in a real name.
 *
 * Empty values are treated as valid so that optional fields pass; requiredness
 * is expressed separately in the Yup schema.
 *
 * @param value - the submitted value
 * @param profile - which character set applies to this field
 * @returns true when the value is free of links and disallowed characters
 */
export const isValidNameField = (value: string | null | undefined, profile: NameFieldProfile): boolean => {
  if (!value || value.trim() === '') {
    return true;
  }

  if (value.length > MAX_LENGTH[profile]) {
    return false;
  }

  if (!ALLOWED_CHARACTERS[profile].test(value)) {
    return false;
  }

  return !BARE_DOMAIN.test(value);
};

/**
 * Yup string schema that rejects names the backend would refuse, with the same
 * wording as the backend's error.
 *
 * @param fieldLabel - human-readable field name used in the error message
 * @param profile - which character set applies to this field
 */
export const nameFieldSchema = (fieldLabel: string, profile: NameFieldProfile) =>
  Yup.string().test(
    'valid-name-field',
    `${fieldLabel} cannot contain links, web addresses, or special characters.`,
    (value) => isValidNameField(value, profile),
  );
